import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingGuard, SistaId, useRequiredValidation, useSafeSistaAsync, } from "@sista/library-stasi";
import { useForm, useWatch } from "react-hook-form";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { RenderTags, RenderTagsWithMenu } from "@sista/service-tagger-ui";
import { Box, Stack } from "@mui/system";
import { Button, Checkbox, Divider, FormControlLabel, Grid, Typography, } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ArrowBack, History } from "@mui/icons-material";
import { IdmClientContext } from "@sista/idm-client";
import { DistaConfirmDialog } from "@sista/dista-ui";
import { SelectNamespace } from "../components/parts/SelectNamespace";
import { SingleTextDataComponent } from "../components/parts/SingleTextComponent";
import { TextHistoryDialog } from "../components/dialogs/TextHistoryDialog";
import { useSpravaTextuClient } from "../util/useSpravaTextuClient";
import { useIsAdmin } from "../util/useIsAdmin";
import { AddTextVariantDialog, } from "../components/dialogs/AddTextVariantDialog";
import { applyVariantUrns } from "../components/parts/SelectVariantUrns";
import { isProduction } from "../components/screens/SpravaTextuAdminScreen";
const taggerNamespace = "spratex";
const iconSize = { width: "20px", height: "20px" };
export const TextDTOForm = ({ canEdit, canTranslate, canView, supportedLanguages, textData, selectedNamespaces, tree, onTextDelete, onSuccess, onMoreTexts, resetingFormOnSucessForNextText, onReset, onClose, }) => {
    /* namespace: string;
      key: string;
      taggerIds: string[];
      state: "NEW" | "UNDER_REVIEW" | "APPROVED" | "DEPRECATED";
      lastChange: string;
      texts: {
          cs: string;
          en?: string;
      };
      plannedUpdate?: string;
      dateDeleted?: string;
      providerUrn?: string;
      programUrn?: string;
      callForProposalUrn?: string; */
    const [showAddVariantDialog, setShowAddVariantDialog] = useState(false);
    const [showTextHistoryDialog, setShowTextHistoryDialog] = useState(false);
    const spratex = useSpravaTextuClient();
    const [getModelCall, getModelState] = useSafeSistaAsync(spratex.getModels);
    const [getTextCall, getTextState] = useSafeSistaAsync(spratex.getText);
    const { loggedAccount } = useContext(IdmClientContext);
    const isAdmin = useIsAdmin();
    const canViewS = canView ?? true;
    const canEditS = canEdit ?? true;
    const canTranslateS = canTranslate ?? true;
    const notTacrProvider = useMemo(() => {
        return loggedAccount.providerId !== "tacr";
    }, [loggedAccount.providerId]);
    const isBaseVariant = useCallback((text) => {
        return (text.providerUrn == null &&
            text.programUrn == null &&
            text.cfpUrn == null &&
            (text.providerUrns?.length ?? 0) === 0 &&
            (text.programUrns?.length ?? 0) === 0 &&
            (text.cfpUrns?.length ?? 0) === 0);
    }, []);
    const isVisibleForProvider = useCallback((text) => {
        if (loggedAccount.providerId === "tacr")
            return true;
        const ownProviderUrn = new SistaId("providers", loggedAccount.providerId).asUrn();
        if (text.providerUrns != null && text.providerUrns.length > 0) {
            return text.providerUrns.includes(ownProviderUrn);
        }
        return text.providerUrn == null || text.providerUrn === ownProviderUrn;
    }, [loggedAccount.providerId]);
    useEffect(() => {
        if (textData == null)
            return;
        getTextCall({
            namespace: textData.namespace,
            key: textData.key,
        });
    }, [getTextCall, textData]);
    const textDataValues = useMemo(() => {
        if (getTextState.value != null) {
            return getTextState.value;
        }
    }, [getTextState.value]);
    const form = useForm({
        defaultValues: textDataValues
            ? {
                namespace: textDataValues.namespace,
                key: textDataValues.key,
                taggerIds: textDataValues.taggerIds ?? [],
                state: textDataValues.state,
                texts: textDataValues.texts ?? [],
                flags: textDataValues.flags ?? [],
            }
            : {
                namespace: selectedNamespaces
                    ? selectedNamespaces.length === 1
                        ? selectedNamespaces[0]
                        : undefined
                    : undefined,
                key: "",
                taggerIds: [],
                state: null,
                texts: [],
                flags: [],
            },
    });
    useEffect(() => {
        if (textDataValues != null) {
            form.reset(textDataValues);
        }
    }, [form, textDataValues]);
    const watchedTexts = useWatch({ control: form.control, name: "texts" }) ?? [];
    const { t } = useTranslation("sprava-textu");
    const required = useRequiredValidation();
    const selectedNamespaceValue = form.watch("namespace");
    const tagIds = form.watch("taggerIds");
    const [showDeleteTextConfirmDialog, setShowDeleteTextConfirmDialog] = useState(false);
    const defaultTextValue = useMemo(() => {
        if (getTextState.value == null)
            return null;
        return getTextState.value.texts?.find((text) => text.providerUrn == null &&
            text.programUrn == null &&
            text.cfpUrn == null);
    }, [getTextState.value]);
    useEffect(() => {
        getModelCall({});
    }, [getModelCall]);
    useEffect(() => {
        if (getModelState.value == null)
            return;
        const model = getModelState.value;
        //console.log("Model loaded:", model);
    }, [getModelState.value]);
    useEffect(() => {
        if (resetingFormOnSucessForNextText) {
            form.reset();
            form.setValue("namespace", selectedNamespaceValue);
            setMoreTexts(false);
            onReset?.();
        }
    }, [form, resetingFormOnSucessForNextText, selectedNamespaceValue, onReset]);
    const [moreTexts, setMoreTexts] = useState(false);
    const handleSubmit = (data) => {
        if (onSuccess) {
            if (moreTexts) {
                onMoreTexts(data);
            }
            else {
                onSuccess(data);
            }
        }
    };
    const markdownChecked = form.watch("flags")?.includes("MARKDOWN") ?? false;
    const addVariantHandler = useCallback((data) => {
        setShowAddVariantDialog(false);
        const newVariantText = applyVariantUrns(defaultTextValue, data);
        newVariantText.composedId =
            "addedVariant|" +
                (newVariantText.providerUrns ?? [newVariantText.providerUrn]).join(",") +
                "|" +
                (newVariantText.programUrns ?? [newVariantText.programUrn]).join(",") +
                "|" +
                (newVariantText.cfpUrns ?? [newVariantText.cfpUrn]).join(",");
        const prevTexts = form.getValues("texts");
        form.setValue("texts", [...prevTexts, newVariantText]);
        form.reset(form.getValues());
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setValue, form.getValues, defaultTextValue]);
    const onDeleteVariantHandler = useCallback((data) => {
        const allTexts = form.getValues("texts");
        const newAllTexts = allTexts.filter((text) => text.composedId !== data.composedId);
        form.setValue("texts", newAllTexts);
        form.reset(form.getValues());
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.getValues, form.setValue]);
    const onChangeHandler = useCallback((data) => {
        const allTexts = form.getValues("texts");
        const newAllTexts = [];
        if (allTexts.length > 0) {
            for (let i = 0; i < allTexts.length; i++) {
                const text = allTexts[i];
                if (text.composedId !== data.composedId) {
                    newAllTexts.push(text);
                }
            }
            newAllTexts.push(data);
        }
        form.setValue("texts", newAllTexts);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.getValues, form.setValue]);
    return (_jsx(FormContainer, { formContext: form, onSuccess: handleSubmit, children: (textDataValues == null || textDataValues) && (_jsxs(Stack, { direction: "column", spacing: 2, sx: {
                height: textDataValues
                    ? isProduction
                        ? "calc(100vh - 246px)"
                        : "calc(100vh - 326px)"
                    : "auto",
            }, children: [_jsx(Box, { sx: { flex: "1 1 auto", overflow: "auto" }, children: _jsxs(Stack, { direction: "column", spacing: 2, sx: { my: 1 }, children: [_jsxs(Grid, { container: true, sx: { width: "100%" }, children: [_jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(TextFieldElement, { sx: { pr: 2, width: "100%" }, name: "key", label: t("sprava-textu.textDTOForm.key", "Klíč textu"), validation: required, disabled: !canEdit || notTacrProvider }) }), _jsxs(Grid, { item: true, xs: 12, sm: 6, sx: { textAlign: "right" }, children: [textDataValues?.namespace == null && (_jsx(SelectNamespace, { sxProps: {}, multiple: false, onNamespacesSelected: (namespace) => {
                                                    if (typeof namespace === "object") {
                                                        if (Array.isArray(namespace)) {
                                                            return form.setValue("namespace", namespace[0]?.namespace);
                                                        }
                                                        return form.setValue("namespace", namespace.namespace);
                                                    }
                                                    return;
                                                } })), textDataValues?.namespace != null && (_jsx(TextFieldElement, { sx: { width: "100%" }, name: "namespace", label: t("sprava-textu.textDTOForm.namespace", "Jmenný prostor"), disabled: true, defaultValue: textDataValues.namespace }))] })] }), _jsx(Stack, { direction: "row", spacing: 0, sx: {}, children: canEdit && !notTacrProvider ? (_jsx(RenderTagsWithMenu, { namespace: taggerNamespace, tagIds: tagIds, onAssign: (selectedIds) => form.setValue("taggerIds", selectedIds), alignButton: "left" })) : tagIds.length === 0 ? (_jsx(Typography, { variant: "body1", children: t("sprava-textu.textDTOForm.noTags", "Zatím nejsou nastaveny žádné štítky.") })) : (_jsx(RenderTags, { namespace: taggerNamespace, tagIds: tagIds })) }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(FormControlLabel, { label: t("sprava-textu.textDTOForm.dictionary", "Slovník"), control: _jsx(Checkbox, { checked: form.watch("flags")?.includes("DICTIONARY") ?? false, onChange: (e) => {
                                                if (e.target.checked) {
                                                    const currentFlags = form.getValues("flags") || [];
                                                    form.setValue("flags", [
                                                        ...currentFlags,
                                                        "DICTIONARY",
                                                    ]);
                                                }
                                                else {
                                                    const currentFlags = form.getValues("flags") || [];
                                                    form.setValue("flags", currentFlags.filter((flag) => flag !== "DICTIONARY"));
                                                }
                                            }, disabled: !canEdit || notTacrProvider }) }), _jsx(FormControlLabel, { label: t("sprava-textu.textDTOForm.markdown", "Markdown"), control: _jsx(Checkbox, { checked: markdownChecked, onChange: (e) => {
                                                if (e.target.checked) {
                                                    const currentFlags = form.getValues("flags") || [];
                                                    form.setValue("flags", [...currentFlags, "MARKDOWN"]);
                                                }
                                                else {
                                                    const currentFlags = form.getValues("flags") || [];
                                                    form.setValue("flags", currentFlags.filter((flag) => flag !== "MARKDOWN"));
                                                }
                                            }, disabled: !canEdit || notTacrProvider }) })] }), _jsxs(LoadingGuard, { state: getModelState, children: [textDataValues == null && getModelState.value && tree && (_jsx(SingleTextDataComponent, { canEdit: canEdit, canTranslate: canTranslate, resetingFormOnSucessForNextText: resetingFormOnSucessForNextText, textData: null, tree: tree, model: getModelState.value, onChange: (text) => {
                                            form.setValue("texts", [text]);
                                        }, languages: supportedLanguages ?? ["cs", "en"], markdowChecked: markdownChecked })), textDataValues?.texts?.length === 0 &&
                                        tree &&
                                        getModelState.value && (_jsx(SingleTextDataComponent, { canEdit: canEditS, canTranslate: canTranslateS, resetingFormOnSucessForNextText: resetingFormOnSucessForNextText, textData: null, tree: tree, model: getModelState.value, onChange: (text) => {
                                            form.setValue("texts", [text]);
                                        }, languages: supportedLanguages ?? ["cs", "en"], markdowChecked: markdownChecked })), textDataValues?.texts != null &&
                                        tree &&
                                        getModelState.value &&
                                        [...watchedTexts]
                                            .sort((a, b) => {
                                            const aIsBase = isBaseVariant(a);
                                            const bIsBase = isBaseVariant(b);
                                            return aIsBase === bIsBase ? 0 : aIsBase ? -1 : 1;
                                        })
                                            .map((text, index) => (_jsx(_Fragment, { children: isVisibleForProvider(text) ? (_jsxs(_Fragment, { children: [_jsx(SingleTextDataComponent, { canEdit: canEditS, canTranslate: canTranslateS, canView: canViewS, textData: text, tree: tree, model: getModelState.value, onChange: (data) => {
                                                            return onChangeHandler(data);
                                                        }, onDelete: (data) => {
                                                            return onDeleteVariantHandler(data);
                                                        }, languages: supportedLanguages ?? ["cs", "en"], markdowChecked: markdownChecked }, index), watchedTexts.length > 1 && (_jsx(Divider, { sx: { my: 1 } })), textDataValues != null && isBaseVariant(text) && (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", onClick: () => setShowAddVariantDialog(true), disabled: !canEdit, children: t("sprava-textu.textDTOForm.addVariant", "Přidat variantu") })] }), _jsx(Divider, {})] }))] })) : null }))), showAddVariantDialog && tree != null && (_jsx(AddTextVariantDialog, { canEdit: canEditS, tree: tree, onClose: () => setShowAddVariantDialog(false), onAddVariant: (data) => addVariantHandler(data) }))] })] }) }), _jsxs(Stack, { direction: "row", spacing: 2, sx: {
                        mt: 2,
                        pt: 2,
                        flex: "0 0 auto",
                        borderTop: "1px solid lightgray",
                    }, children: [onClose ? (_jsx(Button, { variant: "text", onClick: () => {
                                onClose();
                            }, children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(ArrowBack, { sx: iconSize }), _jsx(Typography, { children: t("sprava-textu.textDTOForm.close", "Zavřít").toLocaleUpperCase() })] }) })) : null, isAdmin && textData != null && (_jsx(Button, { variant: "text", onClick: () => setShowTextHistoryDialog(true), children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(History, { sx: iconSize }), _jsx(Typography, { children: t("sprava-textu.textDTOForm.history", "Historie").toLocaleUpperCase() })] }) })), showTextHistoryDialog && textData != null && (_jsx(TextHistoryDialog, { namespace: textData.namespace, textKey: textData.key, onClose: () => setShowTextHistoryDialog(false) })), _jsx(Box, { sx: { flexGrow: 1 } }), canEdit && textData != null && (_jsx(Button, { variant: "text", color: "error", onClick: () => setShowDeleteTextConfirmDialog(true), children: t("sprava-textu.textDTOForm.delete", "Odstranit").toLocaleUpperCase() })), showDeleteTextConfirmDialog && textData != null && (_jsx(DistaConfirmDialog, { open: showDeleteTextConfirmDialog, onClose: () => setShowDeleteTextConfirmDialog(false), okAction: {
                                type: "button",
                                children: t("sprava-textu.textDTOForm.confirmDialog.ok", "Ano"),
                                onClick: () => {
                                    setShowDeleteTextConfirmDialog(false);
                                    onTextDelete(textData);
                                },
                            }, cancelAction: {
                                type: "button",
                                children: t("sprava-textu.textDTOForm.confirmDialog.cancel", "Zrušit"),
                                onClick: () => {
                                    setShowDeleteTextConfirmDialog(false);
                                },
                            }, title: t("sprava-textu.textDTOForm.deleteText", "Smazat text"), children: _jsxs(Stack, { direction: "column", spacing: 1, children: [_jsx(Typography, { children: t("sprava-textu.textDTOForm.deleteTextConfirmation", "Opravdu chcete smazat text s tímto klíčem: ") }), _jsx(Typography, { variant: "body1", sx: { fontWeight: "bold" }, children: textData.key })] }) })), textData == null && (_jsx(Button, { variant: "contained", type: "submit", children: textData == null
                                ? t("sprava-textu.textDTOForm.add", "Vložit").toLocaleUpperCase()
                                : t("sprava-textu.textDTOForm.update", "Aktualizovat text").toLocaleUpperCase() })), (canEdit || canTranslate) && onMoreTexts ? (_jsx(Button, { variant: "contained", type: "submit", onClick: () => setMoreTexts(true), children: textData == null
                                ? t("sprava-textu.textDTOForm.addAndMore", "Vložit a další").toLocaleUpperCase()
                                : t("sprava-textu.textDTOForm.updateAndMore", "Uložit a další").toLocaleUpperCase() })) : null] })] })) }));
};
